import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import axios from '@helpers/axios/private.axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Input, CustomSelect } from "../../../../library";
import { Box } from "@mui/material";

const InfoBlock = ({ selectedEvent }) => {
  const exampleUrl = `https://postback.aff?event=${selectedEvent}&YourClickID={click_id}`;

  return (
    <Box sx={{
      my: 2, p: 2,
      background: 'rgba(0, 122, 255, 0.1)',
      border: '1px solid rgba(0, 122, 255, 0.5)',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#E0E0E0'
    }}>
      <p style={{ margin: 0 }}>
        Paste your tracking URL here with our macros inside curly braces {'{}'}.
        Make sure to replace your internal tokens with our macros from the list below.
      </p>
      <p style={{ marginTop: '10px', marginBottom: 0 }}>
        <span style={{ color: '#888' }}>Example:</span>
        <code style={{ color: '#A5D6A7', background: 'transparent', marginLeft: '5px' }}>
          {exampleUrl}
        </code>
      </p>
    </Box>
  );
};

const MacrosHelper = ({ macros }) => {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box sx={{ my: 3, p: 2, background: '#2C2C2E', borderRadius: '8px' }}>
      <p style={{ margin: 0, marginBottom: '12px', fontSize: '14px' }}>
        Use available macros in your URL. Click to copy.
      </p>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {macros.map(({ macro, description }) => (
          <code
            key={macro}
            title={description}
            onClick={() => copyToClipboard(macro)}
            style={{
              background: '#444',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontFamily: 'monospace',
            }}
          >
            {macro}
          </code>
        ))}
      </Box>
    </Box>
  );
};


export const CreatePostback = () => {
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const { control, handleSubmit, register, watch, reset } = useForm({
    defaultValues: {
      event: 'registration',
      campaign_name: '',
      url: '',
      status: true,
    }
  });

  useEffect(() => {
    if (isEditMode) {
      setLoading(true);
      axios.get(`/postbacks/${id}`)
        .then(resp => {
          reset(resp.data.data);
        })
        .catch(err => console.error("Failed to fetch postback data:", err))
        .finally(() => setLoading(false));
    }
  }, [id, isEditMode, reset]);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const resp = await axios.get('/common/companies');
        const options = resp.data.data.map(name => ({ value: name, label: name }));
        setCampaigns(options);
      } catch (e) { console.error("Failed to fetch campaigns", e); }
    };
    fetchCampaigns();
  }, []);

  const selectedEvent = watch('event');

  const onSubmit = async (data) => {
    setLoading(true);
    const request = isEditMode
      ? axios.put(`/postbacks/${id}`, data)
      : axios.post('/postbacks', data);

    try {
      await request;
      navigate('/postbacks');
    } catch (error) {
      console.error("Failed to save postback:", error);
      alert('Error saving postback.');
    } finally {
      setLoading(false);
    }
  };

  // Визначаємо, які макроси показувати
  const getAvailableMacros = () => {
    const macros = [
      { macro: '{event}', description: 'Event name (e.g., ftd)' },
      { macro: '{click_id}', description: 'Unique click identifier' },
      { macro: '{user_id}', description: 'Your referral\'s unique ID' },
      { macro: '{campaign_name}', description: 'Campaign name' },
    ];

    if (selectedEvent === 'registration' || selectedEvent === 'ftd') {
      macros.push({ macro: '{country}', description: 'User country code (e.g., US)' });
    }

    if (selectedEvent === 'ftd' || selectedEvent === 'deposit') {
      macros.push({ macro: '{amount}', description: 'Current deposit amount' });
    }

    if (selectedEvent === 'deposit') {
      macros.push({ macro: '{sumdep}', description: 'Total sum of all deposits' });
    }

    return macros;
  };

  const eventOptions = [
    { value: 'registration', label: 'Registration' },
    { value: 'ftd', label: 'First Deposit' },
    { value: 'deposit', label: 'Re-Deposit' },
  ];

  return (
    <div style={{ maxWidth: 1100 }}>
      <h1>{isEditMode ? 'Edit Postback' : 'Add New Postback'}</h1>
      <form onSubmit={handleSubmit(onSubmit)}>

        <CustomSelect
          control={control}
          name="event"
          label="Event:"
          options={eventOptions}
        />

        <CustomSelect
          control={control}
          name="campaign_name"
          label="Campaign (optional):"
          options={campaigns}
          placeholder="Global (All Campaigns)"
        />

        <Input
          data={{
            label: "URL:",
            type: "text",
            settings: { ...register("url", { required: true }) },
            name: "url",
          }}
        />

        <InfoBlock selectedEvent={selectedEvent} />

        <MacrosHelper macros={getAvailableMacros()} />

        <Button type="submit" color="orange" isLoading={loading}>Save Postback</Button>
      </form>
    </div>
  );
};